<script setup lang="ts">
import { Form, router } from "@inertiajs/vue3"
import { ref, watch } from "vue"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { approveManagerTeleworkPath, rejectManagerTeleworkPath } from "@/routes"
import type { ManagerTelework } from "@/types/serializers"

const props = defineProps<{
  data: ManagerTelework
}>()

const open = ref(false)
const showRejectForm = ref(false)
const rejectReason = ref("")

function closeDialog() {
  showRejectForm.value = false
  rejectReason.value = ""
  open.value = false
}

function approve() {
  router.visit(approveManagerTeleworkPath(props.data.id), {
    method: "post",
    onSuccess: () => closeDialog(),
  })
}

function openRejectForm() {
  showRejectForm.value = true
}

function cancelReject() {
  showRejectForm.value = false
  rejectReason.value = ""
}

watch(open, (value) => {
  if (!value) cancelReject()
})
</script>

<template>
  <Dialog v-model:open="open">
    <DialogTrigger>
      <Button variant="link" class="h-4 px-0">Abrir</Button>
    </DialogTrigger>
    <DialogContent>
      <DialogHeader>
        <DialogTitle :class="cn('font-bold')">
          Solicitud ID: {{ data.id }}
        </DialogTitle>
        <DialogDescription>
          Revisión de reembolso de teletrabajo
        </DialogDescription>
      </DialogHeader>
      <div class="flex flex-col gap-2">
        <Card>
          <CardContent>
            <p class="mb-4 font-bold">Información del Empleado</p>

            <div class="grid grid-cols-2">
              <div class="flex flex-col gap-0.5">
                <p class="text-muted-foreground text-xs">Empleado:</p>
                <p>{{ data.user.full_name }}</p>
              </div>

              <div class="flex flex-col gap-0.5">
                <p class="text-muted-foreground text-xs">Departamento:</p>
                <p>{{ data.user.department.toUpperCase() }}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p class="mb-4 font-bold">Detalles de la Compra</p>

            <div class="grid grid-cols-2">
              <div class="flex flex-col gap-0.5">
                <p class="text-muted-foreground text-xs">Categoría:</p>
                <p>{{ data.item_type }}</p>
              </div>

              <div class="flex flex-col gap-0.5">
                <p class="text-muted-foreground text-xs">Importe ticket:</p>
                <p>{{ data.price }}€</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p class="mb-4 font-bold">Comprobante</p>

            <a
              :href="data.factura"
              target="_blank"
              download
              class="text-primary underline"
            >
              Ver Comprobante
            </a>
          </CardContent>
        </Card>
      </div>
      <DialogFooter v-if="data.status === 'pending'">
        <Form
          v-if="showRejectForm"
          :action="rejectManagerTeleworkPath(data.id)"
          method="post"
          class="w-full space-y-3"
          disable-while-processing
          :options="{ preserveScroll: true, preserveState: true }"
          @success="closeDialog"
          #default="{ processing, errors }"
        >
          <div class="space-y-1">
            <p class="text-sm font-medium">Motivo de rechazo</p>
            <Textarea
              v-model="rejectReason"
              name="reject_reason"
              rows="3"
              placeholder="Escribe el motivo del rechazo"
              :disabled="processing"
            />
            <p v-if="errors.reject_reason" class="text-destructive text-sm">
              {{ errors.reject_reason }}
            </p>
          </div>
          <div class="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              :disabled="processing"
              @click="cancelReject"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="destructive"
              :disabled="processing || !rejectReason.trim()"
            >
              Rechazar
            </Button>
          </div>
        </Form>
        <template v-else>
          <Button variant="destructive" size="lg" @click="openRejectForm">
            Rechazar
          </Button>
          <Button size="lg" @click="approve">Aprobar</Button>
        </template>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
